import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import GetS12nIdsByCourseId from 'bundles/enroll-common/queries/GetS12nIdsByCourseId.graphql';
import type {
  GetS12nIdsByCourseIdQuery as GetS12nIdsByCourseIdQueryData,
  GetS12nIdsByCourseIdQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetS12nIdsByCourseId';
import type { ProductOwnershipsReturnType } from 'bundles/entitlements-common/hooks/useProductOwnerships';
import useProductOwnerships from 'bundles/entitlements-common/hooks/useProductOwnerships';
import { isCourseGrantedByFreeTrial } from 'bundles/entitlements-common/utils/withCourseOwnership';
import { useNaptime } from 'bundles/naptimejs';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for this module.
import SubscriptionTrialsV1 from 'bundles/naptimejs/resources/subscriptionTrials.v1';

type PropsFromSubscriptionTrials = {
  subscriptionTrials?: Array<SubscriptionTrialsV1>;
};

type CourseOwnershipReturnType = {
  loading?: boolean;
  course: { s12nIds: Array<string> };
  ownsCourse: boolean;
  isFreeTrial?: boolean;
  subscriptionTrials?: Array<SubscriptionTrialsV1>;
  courseOwnership: ProductOwnershipsReturnType['courseOwnership'];
};

type UseGetCourseS12nIdsParams = {
  courseId: string;
  skip?: boolean;
};

/**
 * Hook to fetch specialization IDs for a given course.
 *
 * @param courseId - The ID of the course to fetch specialization IDs for
 * @param skip - Whether to skip the API fetch
 * @returns Object containing course data with specialization IDs and loading state
 */
export const useGetCourseS12nIds = ({ courseId, skip = false }: UseGetCourseS12nIdsParams) => {
  const { data, loading } = useQuery<GetS12nIdsByCourseIdQueryData, GetS12nIdsByCourseIdQueryVariables>(
    GetS12nIdsByCourseId,
    {
      variables: {
        id: courseId,
      },
      skip: skip || !user.isAuthenticatedUser() || !courseId,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const course = data?.Course?.queryById;
  const s12nIds = course?.specializations?.map(({ id }) => id) ?? [];

  return { ...course, s12nIds, loading };
};

/**
 * Hook to fetch subscription trials for a user if they own a course.
 *
 * @param ownsCourse - Whether the user owns the course
 * @returns Object containing subscription trials data and loading state
 */
const useGetSubscriptionTrials = (ownsCourse: boolean) => {
  const {
    data: { subscriptionTrials },
    pending,
  } = useNaptime<PropsFromSubscriptionTrials>(() => {
    if (!user.isAuthenticatedUser() || !ownsCourse) {
      return {};
    }

    return {
      subscriptionTrials: ownsCourse
        ? SubscriptionTrialsV1.finder('findByUser', {
            params: {
              userId: user.get().id,
            },
            fields: ['createdAt', 'endsAt', 'productType', 'productItemId'],
          })
        : undefined,
    };
  }, [ownsCourse]);

  return {
    subscriptionTrials,
    loading: pending,
  };
};

/**
 * Custom hook to replace `withCourseOwnership` HOC.
 * Provides course ownership information including free trial status.
 *
 * @param courseId - The ID of the course to check ownership for
 * @returns Object containing course ownership data, and loading state
 */
const useCourseOwnership = (courseId: string): CourseOwnershipReturnType => {
  const { courseOwnership, loading: loadingCourseOwnership } = useProductOwnerships({ courseId });
  const ownsCourse = courseOwnership?.owns ?? false;

  const course = useGetCourseS12nIds({ courseId });

  const { subscriptionTrials, loading: loadingSubscriptionTrials } = useGetSubscriptionTrials(ownsCourse);

  const isFreeTrial =
    subscriptionTrials && isCourseGrantedByFreeTrial(courseId, course.s12nIds || [], subscriptionTrials);

  return {
    loading: loadingCourseOwnership || loadingSubscriptionTrials || course?.loading,
    course,
    ownsCourse,
    isFreeTrial,
    subscriptionTrials,
    courseOwnership,
  };
};

export default useCourseOwnership;
